"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { trackPaymentOutcomeEvent } from "@/lib/payments/payment-outcome-analytics";
import { usePaymentOutcomeTracking } from "./use-payment-outcome-tracking";
import {
  DEFAULT_NEW_QR_LABEL,
  formatCountdown,
  requestQrisReissue,
  saveQrCanvasImage,
  type QrisLabels,
} from "./qris-payment-step.helpers";
import {
  DEFAULT_RAIL,
  PaymentRailsPanel,
  type PaymentRail,
} from "./payment-rails-panel";
import {
  DEFAULT_VA_BANK,
  type InitialVirtualAccount,
  type VaBank,
} from "./use-virtual-account";
import {
  QrisExpiredView,
  QrisFailedView,
  QrisSuccessView,
} from "./qris-payment-step.views";
import {
  latestTimestamp,
  QRIS_FALLBACK_EXPIRY_SECONDS,
  useQrisCountdown,
} from "./use-qris-countdown";

export type { QrisLabels };

interface PaymentStatusResponse {
  status: "pending" | "completed" | "failed";
  qrString?: string | null;
  invoiceUrl?: string | null;
  /** The QR is dead but another rail is still payable; reissue is the fix. */
  qrisFailed?: boolean;
  /** Dev-only: true when the server runs against the payment sandbox. */
  canSimulate?: boolean;
  /** Authoritative QR expiry (ISO) reported by the server. */
  expiresAt?: string | null;
  tier?: string | null;
  amount?: number | null;
}

interface QrisPaymentStepProps {
  paymentId: string;
  qrString: string | null;
  invoiceUrl?: string | null;
  amount: number;
  labels: QrisLabels;
  onSuccess: (paymentId: string) => void;
  onRetry?: () => void;
  /** Called when the payment reaches a terminal state (expired or failed).
   * Consumers use this to suppress duplicate abandonment tracking. */
  onTerminalState?: () => void;
  /** Authoritative expiry (ISO) from the payment create response. Status polls
   * also carry it, so consumers may omit this. */
  expiresAt?: string | null;
  /** Fallback countdown when no server expiry is known yet. */
  expiresInSeconds?: number;
  /** Payment tier for analytics; poll responses override it when present. */
  tier?: string;
  /** Guest checkout: claim token passed as query param for unauthenticated status polling. */
  claimToken?: string;
  /** Bank virtual account issued when the checkout was created, if any. */
  initialVirtualAccount?: InitialVirtualAccount | null;
  /**
   * Rail the buyer lands on. Defaults to bank transfer, which is what converts
   * on mobile; a surface where scanning is natural (a desktop with a phone in
   * hand) can open on QRIS instead.
   */
  initialRail?: PaymentRail;
  /**
   * "stacked" (default): single centered column, used inside narrow cards and
   * dialogs. "split": QR on the left, payment details on the right on sm+
   * screens, keeping the whole step inside the viewport without scrolling.
   */
  layout?: "stacked" | "split";
}

const POLL_INTERVAL_MS = 3500;

function useRefetchOnVisibilityChange(refetch: () => Promise<unknown>) {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetch]);
}

function useSyncRef<T>(ref: MutableRefObject<T>, value: T) {
  useEffect(() => {
    ref.current = value;
  }, [ref, value]);
}

export function QrisPaymentStep({
  paymentId,
  qrString,
  invoiceUrl,
  amount,
  labels,
  onSuccess,
  onRetry,
  expiresAt,
  expiresInSeconds = QRIS_FALLBACK_EXPIRY_SECONDS,
  tier,
  claimToken,
  initialVirtualAccount,
  initialRail = DEFAULT_RAIL,
  layout = "stacked",
  onTerminalState,
}: QrisPaymentStepProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rail, setRail] = useState<PaymentRail>(initialRail);
  const [selectedBank, setSelectedBank] = useState<VaBank>(DEFAULT_VA_BANK);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isReissuing, setIsReissuing] = useState(false);
  const [reissueFailed, setReissueFailed] = useState(false);
  const [reissued, setReissued] = useState<{
    qrString: string | null;
    expiresAt: string;
  } | null>(null);
  const phaseRef = useRef<"active" | "grace" | "expired">("active");

  const { data, refetch } = useQuery<PaymentStatusResponse>({
    queryKey: ["payment-status", paymentId, claimToken ?? ""],
    enabled: Boolean(paymentId),
    queryFn: async () => {
      const statusUrl = new URL(
        `/api/payment/${paymentId}/status`,
        window.location.origin,
      );
      if (claimToken) {
        statusUrl.searchParams.set("claimToken", claimToken);
      }
      const response = await fetch(statusUrl.toString(), {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch payment status");
      }

      return (await response.json()) as PaymentStatusResponse;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") {
        return false;
      }
      // Local expiry never kills polling: the grace window keeps checking so a
      // buyer who paid at the last second still lands on success. Only genuine
      // expiry (grace elapsed) stops the interval.
      if (phaseRef.current === "expired") {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });

  useRefetchOnVisibilityChange(refetch);

  const status = data?.status ?? "pending";
  const isSuccess = status === "completed";
  const serverFailed = status === "failed";
  // Once a poll has answered, its values are authoritative, including null: a
  // dead QR or a lapsed hosted link must not fall back to what the create
  // response handed us.
  const effectiveQr =
    reissued?.qrString ?? (data ? (data.qrString ?? null) : qrString);
  const effectiveInvoiceUrl = data
    ? (data.invoiceUrl ?? null)
    : (invoiceUrl ?? null);
  const qrisFailed = !reissued && Boolean(data?.qrisFailed);
  const canSimulate = data?.canSimulate ?? false;

  // The countdown derives from the server's authoritative expiry (create
  // response, status polls, or a reissued QR, whichever is freshest), falling
  // back to a local deadline only when the server omits it. Terminal poll
  // responses omit expiresAt, so the last polled value is remembered: a
  // "failed" poll after the countdown hit zero must read as expiry, not reset
  // the clock to the fallback.
  const lastPolledExpiryRef = useRef<string | null>(null);
  if (data?.expiresAt) {
    lastPolledExpiryRef.current = data.expiresAt;
  }
  const serverDeadlineMs = latestTimestamp(
    expiresAt,
    lastPolledExpiryRef.current,
    reissued?.expiresAt,
  );
  const { secondsLeft, phase } = useQrisCountdown({
    serverDeadlineMs,
    fallbackSeconds: expiresInSeconds,
    running: !isSuccess && !serverFailed,
  });

  useSyncRef(phaseRef, phase);

  const isGuest = Boolean(claimToken);
  const eventTier = data?.tier ?? tier ?? null;
  const eventAmount = data?.amount ?? amount;

  // A server-reported failure after the countdown hit zero is an expiry (the
  // provider QR lapsed), not a genuine payment failure.
  //
  // The countdown belongs to the QR. A buyer on the bank-transfer rail holds a
  // virtual account that is live for VA_WINDOW_SECONDS, so tearing their screen
  // down at the 30-minute mark would kill a payment they can still complete.
  //
  // A provider-reported dead QR while another rail is still open (`qrisFailed`)
  // is the same buyer-facing situation as expiry: offer a fresh QR.
  const isExpiredState =
    !isSuccess &&
    rail === "qris" &&
    (phase === "expired" ||
      qrisFailed ||
      (serverFailed && phase !== "active"));
  const isFailedState = !isSuccess && serverFailed && phase === "active";

  const { resetTerminalTracking } = usePaymentOutcomeTracking({
    paymentId,
    isCompleted: status === "completed",
    isFailedState,
    isExpiredState,
    expiryReason: qrisFailed ? "qr_failed" : "countdown",
    tier: eventTier,
    amount: eventAmount,
    isGuest,
    onSuccess,
    onTerminalState,
  });

  const handleReissue = async () => {
    trackPaymentOutcomeEvent(
      "retry_clicked",
      { paymentId, tier: eventTier, amount: eventAmount, guest: isGuest },
      { action: "new_qr" },
    );
    setIsReissuing(true);
    setReissueFailed(false);
    try {
      const fresh = await requestQrisReissue(paymentId, claimToken);
      setReissued({
        qrString: fresh.qrString,
        expiresAt:
          fresh.expiresAt ??
          new Date(Date.now() + expiresInSeconds * 1000).toISOString(),
      });
      resetTerminalTracking();
      await refetch();
    } catch {
      setReissueFailed(true);
    } finally {
      setIsReissuing(false);
    }
  };

  const handleRetry = () => {
    trackPaymentOutcomeEvent(
      "retry_clicked",
      { paymentId, tier: eventTier, amount: eventAmount, guest: isGuest },
      { action: "retry" },
    );
    onRetry?.();
  };

  const handleSimulate = async () => {
    setIsSimulating(true);
    try {
      await fetch(`/api/payment/${paymentId}/simulate`, { method: "POST" });
      // Poll immediately so the modal flips to success without waiting for the
      // next interval tick.
      await refetch();
    } catch {
      // Best-effort dev affordance; the regular polling still resolves it.
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSaveImage = () => {
    saveQrCanvasImage(canvasRef.current, paymentId);
  };

  const countdownLabel = useMemo(
    () => labels.expiresIn.replace("{{time}}", formatCountdown(secondsLeft)),
    [labels.expiresIn, secondsLeft],
  );

  if (isSuccess) {
    return <QrisSuccessView message={labels.statusSuccess} />;
  }

  if (isExpiredState) {
    return (
      <QrisExpiredView
        message={labels.expired}
        newQrLabel={labels.newQr ?? DEFAULT_NEW_QR_LABEL}
        errorMessage={reissueFailed ? labels.statusFailed : null}
        isReissuing={isReissuing}
        onReissue={handleReissue}
      />
    );
  }

  if (isFailedState) {
    return (
      <QrisFailedView
        message={labels.statusFailed}
        retryLabel={labels.retry}
        onRetry={onRetry ? handleRetry : undefined}
      />
    );
  }

  return (
    <PaymentRailsPanel
      paymentId={paymentId}
      {...(claimToken ? { claimToken } : {})}
      rail={rail}
      onRailChange={setRail}
      initialVirtualAccount={initialVirtualAccount ?? null}
      selectedBank={selectedBank}
      onSelectBank={setSelectedBank}
      labels={labels}
      amount={amount}
      effectiveQr={effectiveQr}
      invoiceUrl={effectiveInvoiceUrl}
      canvasRef={canvasRef}
      canSimulate={canSimulate}
      isSimulating={isSimulating}
      onSimulate={handleSimulate}
      onSaveImage={handleSaveImage}
      phase={phase}
      secondsLeft={secondsLeft}
      countdownLabel={countdownLabel}
      layout={layout}
    />
  );
}
